/**
 * Redux slice de l'aperçu d'un concept derrière une marque (chantier C23
 * « Confrontation », SP1 « Définitions côte à côte », spec § 6.4).
 *
 * L'état est indexé par **note et concept** (`byCle[noteId:entreeId]`) : le droit
 * tient à la note (c'est elle qui est lisible), jamais au concept seul — rien n'est
 * réutilisé d'une note à l'autre. Une garde `requestId` écarte la réponse périmée
 * d'un même couple. Un 404 est un état (« indisponible »), pas une erreur : la
 * marque a pu être dévalidée, le partage retiré. Aucune notification globale.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { noteService } from '../../services/noteAPI';
import { organisationAPI } from '../../services/organisationAPI';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import type { ApercuConceptDto } from '../../types/api';
import { porteDeSource, type SourceConcept } from '../../types/sourceConcept';

export type ApercuStatus = 'idle' | 'loading' | 'ready' | 'indisponible' | 'error';

export interface ApercuEntry {
    status: ApercuStatus;
    apercu?: ApercuConceptDto;
    /** Dernier chargement lancé pour ce couple : garde anti-course. */
    requestId: string | null;
}

export interface ApercuConceptState {
    byCle: Record<string, ApercuEntry>;
}

export const initialApercuConceptState: ApercuConceptState = { byCle: {} };

export const cleApercu = (noteId: string, entreeId: string): string => `${noteId}:${entreeId}`;

/** L'argument de la lecture : la source (note OU organisation, jamais les deux ni aucune), le concept, le jeton. */
export type ApercuArg = SourceConcept & { entreeId: string; instance: any };

/** Entrée de repli des sélecteurs : référence stable, sinon re-rendus en boucle. */
const EMPTY_ENTRY: ApercuEntry = Object.freeze({ status: 'idle', requestId: null }) as ApercuEntry;

async function tokenOf(instance: any): Promise<string> {
    const accessToken = await getTokenRedirect(tokenRequest, instance);
    if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
    return accessToken;
}

export const fetchApercuConcept = createAsyncThunk<
    ApercuConceptDto,
    ApercuArg,
    { rejectValue: 'indisponible' | 'error' }
>('apercuConcept/fetch', async (arg, { rejectWithValue }) => {
    try {
        const token = await tokenOf(arg.instance);
        if (arg.organisationId !== undefined) {
            const apercu = await organisationAPI.apercuConcept(arg.organisationId, arg.entreeId, token);
            return apercu ?? rejectWithValue('indisponible');
        }
        return await noteService.getApercuConcept(arg.noteId, arg.entreeId, token);
    } catch (error: any) {
        return rejectWithValue(error?.response?.status === 404 ? 'indisponible' : 'error');
    }
});

const apercuConceptSlice = createSlice({
    name: 'apercuConcept',
    initialState: initialApercuConceptState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchApercuConcept.pending, (state, action) => {
                const cle = cleApercu(porteDeSource(action.meta.arg), action.meta.arg.entreeId);
                const previous = state.byCle[cle];
                state.byCle[cle] = { status: 'loading', apercu: previous?.apercu, requestId: action.meta.requestId };
            })
            .addCase(fetchApercuConcept.fulfilled, (state, action) => {
                const cle = cleApercu(porteDeSource(action.meta.arg), action.meta.arg.entreeId);
                const entry = state.byCle[cle];
                if (!entry || entry.requestId !== action.meta.requestId) return;
                state.byCle[cle] = { status: 'ready', apercu: action.payload, requestId: action.meta.requestId };
            })
            .addCase(fetchApercuConcept.rejected, (state, action) => {
                const cle = cleApercu(porteDeSource(action.meta.arg), action.meta.arg.entreeId);
                const entry = state.byCle[cle];
                if (!entry || entry.requestId !== action.meta.requestId) return;
                state.byCle[cle] = { status: action.payload === 'indisponible' ? 'indisponible' : 'error', requestId: action.meta.requestId };
            });
    },
});

export default apercuConceptSlice.reducer;

// --- Sélecteurs (tolérants : un store partiel rend l'entrée vide) ---
export const selectApercu = (noteId: string, entreeId: string) => (state: any): ApercuEntry =>
    state?.apercuConcept?.byCle?.[cleApercu(noteId, entreeId)] ?? EMPTY_ENTRY;
